// Profile Peek Drawer Styled Components
// Slide-in panel used to preview a kept profile without leaving the swipe deck.
import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';

// Sits above the Keep Pile Review overlay (z-index 10010) so the pile stays
// visually behind the drawer instead of being unmounted.
const DRAWER_BACKDROP_Z = 10020;
const DRAWER_Z = 10021;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const PeekBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: ${DRAWER_BACKDROP_Z};
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

export const PeekDrawer = styled.aside<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 560px;
  height: 100vh;
  height: 100dvh;
  background: var(--bg-card);
  border-left: 1px solid var(--border-light);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.25);
  z-index: ${DRAWER_Z};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateX(${props => (props.$isOpen ? '0' : '100%')});
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 100%;
  }
`;

export const PeekHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-card);
  flex-shrink: 0;
`;

export const PeekTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PeekTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PeekSubtitle = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

export const PeekCloseButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--border-light);
  background: transparent;
  color: var(--text-primary);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: #fff;
  }
`;

export const PeekBody = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* Same reason as the keep pile modal: this drawer opens over feeds and over
     the pile itself, and without containment its scroll hands off to whatever
     is behind once it reaches an end. */
  overscroll-behavior: contain;
  background: var(--bg-primary);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-light);
    border-radius: 4px;
  }
`;

export const PeekStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.md};
  min-height: 60vh;
  padding: ${theme.spacing.xl};
  text-align: center;
  color: var(--text-secondary);
`;

export const PeekSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid var(--border-light);
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const PeekRetryButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary};
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.secondary};
  }
`;

export const PeekFooter = styled.div`
  display: flex;
  align-items: center;
  /* Wraps rather than crushing the labels: callers supply their own action
     set, and three actions plus the back button will not fit one phone row. */
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-top: 1px solid var(--border-light);
  background: var(--bg-card);
  flex-shrink: 0;
  padding-bottom: calc(${theme.spacing.md} + env(safe-area-inset-bottom, 0px));
`;

export const PeekFooterButton = styled.button<{ $variant: 'primary' | 'secondary' | 'danger' }>`
  flex: ${props => (props.$variant === 'danger' ? '0 0 auto' : '1 1 8rem')};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  white-space: nowrap;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => props.$variant === 'primary' && `
    background: ${theme.colors.primary};
    color: #fff;
    &:hover:not(:disabled) { background: ${theme.colors.secondary}; }
  `}

  ${props => props.$variant === 'secondary' && `
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-light);
    &:hover:not(:disabled) { background: var(--bg-secondary); }
  `}

  ${props => props.$variant === 'danger' && `
    background: transparent;
    color: ${theme.colors.error};
    border: 1px solid ${theme.colors.error};
    &:hover:not(:disabled) { background: ${theme.colors.error}; color: #fff; }
  `}
`;

// Quick message composer
//
// Lives in the drawer's footer so a Premium member can send from wherever they
// are — the whole point of the drawer is that looking someone up should not
// cost you your place, and neither should saying hello.
export const PeekComposer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
`;

export const PeekComposerInput = styled.textarea`
  width: 100%;
  min-height: 76px;
  max-height: 160px;
  resize: vertical;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid var(--border-medium);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.4;

  &::placeholder {
    color: var(--text-secondary);
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

export const PeekComposerRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  justify-content: flex-end;
`;

export const PeekComposerHint = styled.span`
  margin-right: auto;
  font-size: 0.8rem;
  color: var(--text-secondary);
`;
